/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import StaticMarkdown from '@/components/charts/StaticMarkdown';

/* Import configuration */
import {
    createMultilineStringConstant,
    STATIC_MARKDOWN_EXAMPLES,
} from '@/components/tabs/layouts/layoutExamples';

interface MarkdownLayoutsTabProps {
    theme: 'light' | 'dark';
}

function MarkdownLayoutsTab({ theme }: MarkdownLayoutsTabProps) {
    const [selectedStaticMarkdownExampleIndex, setSelectedStaticMarkdownExampleIndex] =
        useState(0);
    const [staticMarkdownCodeTab, setStaticMarkdownCodeTab] =
        useState<'jsx' | 'data'>('jsx');

    const selectedStaticMarkdownExample =
        STATIC_MARKDOWN_EXAMPLES[selectedStaticMarkdownExampleIndex];
    const selectedStaticMarkdownCode = createMultilineStringConstant(
        selectedStaticMarkdownExample.codeName,
        selectedStaticMarkdownExample.content,
    );

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader
                sectionKey="markdown"
                description="Markdown renders lightweight explanatory content such as headings, paragraphs, and tables. This is useful for dashboard documentation, contextual notes, and compact data summaries next to charts or KPIs."
            />

            <section className="layouts-section">
                <h3>StaticMarkdown</h3>
                <p>
                    Renders a static markdown string with GitHub Flavored Markdown support.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="static-markdown-example"
                    >
                        Markdown example
                    </label>
                    <select
                        id="static-markdown-example"
                        className="select select-bordered w-auto"
                        value={selectedStaticMarkdownExampleIndex}
                        onChange={(event) => {
                            setSelectedStaticMarkdownExampleIndex(Number(event.target.value));
                        }}
                    >
                        {STATIC_MARKDOWN_EXAMPLES.map((example, index) => (
                            <option key={example.codeName} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button
                            type="button"
                            className={`tab ${staticMarkdownCodeTab === 'jsx' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticMarkdownCodeTab('jsx');
                            }}
                        >JSX</button>
                        <button
                            type="button"
                            className={`tab ${staticMarkdownCodeTab === 'data' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticMarkdownCodeTab('data');
                            }}
                        >Data</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={staticMarkdownCodeTab === 'data'
                            ? selectedStaticMarkdownCode
                            : `<StaticMarkdown
    content={${selectedStaticMarkdownExample.codeName}}
/>`}
                        language={staticMarkdownCodeTab === 'data' ? 'typescript' : 'jsx'}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel">
                    <StaticMarkdown content={selectedStaticMarkdownExample.content} />
                </section>
            </section>
        </div>
    );
}

export default MarkdownLayoutsTab;
